import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search } from 'lucide-react';
import TastingRoomInventoryDetailToolbar from '../components/toolbars/TastingRoomInventoryDetailToolbar';

const TastingRoomInventoryDetail = () => {
    const { logID } = useParams();
    const navigate = useNavigate();
    const [details, setDetails] = useState([]);
    const [selectedType, setSelectedType] = useState('all');
    const [error, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [hasChanges, setHasChanges] = useState(false);
    const isNewReport = !logID;
    
    const productTypes = [
        { value: 'all', label: 'All Products' },
        { value: 'wine', label: 'Wine' },
        { value: 'merchandise', label: 'Merchandise' },
        { value: 'food', label: 'Food & Snacks' },
        { value: 'supplies', label: 'Supplies' }
    ];

    useEffect(() => {
        const fetchInventoryDetails = async () => {
            try {
                let response;
                if (isNewReport) {
                    // Fetch all active inventory items for new report
                    response = await axios.get('/api/tasting-room/inventory');
                    const transformedDetails = response.data.map(item => ({
                        ...item,
                        originalQuantity: item.currentQuantity || 0,
                        currentQuantity: item.currentQuantity || 0,
                        hasChanged: false,
                        difference: 0
                    }));
                    setDetails(transformedDetails);
                } else {
                    // Fetch existing report details
                    response = await axios.get(`/api/tasting-room/inventory-logs/${logID}/details`);
                    const transformedDetails = response.data.map(item => ({
                        ...item,
                        originalQuantity: item.previousQuantity || 0,
                        currentQuantity: item.currentQuantity || 0,
                        hasChanged: false,
                        difference: 0
                    }));
                    setDetails(transformedDetails);
                }
                setIsLoading(false);
            } catch (err) {
                console.error('Error fetching data:', err);
                setError('Failed to load inventory details');
                setIsLoading(false);
            }
        };

        fetchInventoryDetails();
    }, [logID, isNewReport]);

    // Filter details based on search term and type
    const filteredDetails = details.filter(item => {
        const matchesSearch = (
            item.itemName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.brand?.toLowerCase().includes(searchTerm.toLowerCase())
        );

        const matchesType = selectedType === 'all' || item.type === selectedType;
        return matchesSearch && matchesType;
    });

    // Group items by type
    const groupedDetails = filteredDetails.reduce((acc, item) => {
        const type = item.type || 'uncategorized';
        if (!acc[type]) {
            acc[type] = [];
        }
        acc[type].push(item);
        return acc;
    }, {});

    const handleQuantityChange = (inventoryID, newQuantity) => {
        const numValue = parseInt(newQuantity) || 0;
        const updatedDetails = details.map(item => {
            if (item.inventoryID === inventoryID) {
                const difference = numValue - item.originalQuantity;
                return {
                    ...item,
                    currentQuantity: numValue,
                    hasChanged: item.originalQuantity !== numValue,
                    difference
                };
            }
            return item;
        });

        setDetails(updatedDetails);
        setHasChanges(updatedDetails.some(item => item.hasChanged));
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            if (isNewReport) {
                // Create new inventory log
                const response = await axios.post('/api/tasting-room/inventory-logs', {
                    locationID: 1, // Default location or you could add a location selector
                    logDate: new Date().toISOString()
                });
    
                // If creation was successful, now update the details
                if (response.data && response.data.logID) {
                    const changedItems = details.map(item => ({
                        inventoryID: item.inventoryID,
                        previousQuantity: item.originalQuantity,
                        currentQuantity: item.currentQuantity,
                        reason: 'INITIAL_COUNT'
                    }));
    
                    await axios.post(`/api/tasting-room/inventory-logs/${response.data.logID}/update`, {
                        details: changedItems
                    });
                    
                    alert('New inventory report created successfully');
                    navigate('/tasting-room');
                }
            } else {
                // Update existing log (rest of your existing code)
                const changedItems = details
                    .filter(item => item.hasChanged)
                    .map(item => ({
                        inventoryID: item.inventoryID,
                        previousQuantity: item.originalQuantity,
                        currentQuantity: item.currentQuantity,
                        reason: 'ADJUSTMENT'
                    }));
    
                await axios.post(`/api/tasting-room/inventory-logs/${logID}/update`, {
                    details: changedItems
                });
                alert('Inventory updated successfully');
                navigate('/tasting-room');
            }
        } catch (err) {
            console.error('Error saving inventory:', err);
            setError('Failed to save inventory changes');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="container mt-4">
                <div className="d-flex justify-content-center align-items-center h-48">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <TastingRoomInventoryDetailToolbar
                onSave={handleSave}
                onBack={() => navigate('/tasting-room')}
                isSaving={isSaving}
                hasChanges={hasChanges}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                isNewReport={isNewReport}
            />

            <div className="container mt-4">
                {error && (
                    <div className="alert alert-danger alert-dismissible fade show">
                        {error}
                        <button type="button" className="btn-close" onClick={() => setError(null)} />
                    </div>
                )}

                <div className="card mb-4">
                    <div className="card-body">
                        <div className="row align-items-center">
                            <div className="col-md-4">
                                <div className="input-group">
                                    <span className="input-group-text">
                                        <Search size={18} />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search items..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="col-md-4">
                                <select
                                    className="form-select"
                                    value={selectedType}
                                    onChange={(e) => setSelectedType(e.target.value)}
                                >
                                    {productTypes.map(type => (
                                        <option key={type.value} value={type.value}>
                                            {type.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {Object.entries(groupedDetails).map(([type, items]) => (
                    <div key={type} className="card mb-4">
                        <div className="card-header bg-light">
                            <h5 className="mb-0">{type.charAt(0).toUpperCase() + type.slice(1)}</h5>
                        </div>
                        <div className="card-body">
                            <div className="table-responsive">
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th>SKU</th>
                                            <th>Item Name</th>
                                            <th>Brand</th>
                                            <th className="text-end">Price</th>
                                            <th className="text-center">Previous</th>
                                            <th className="text-center">Current</th>
                                            <th className="text-center">Difference</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.map(item => (
                                            <tr 
                                                key={item.inventoryID}
                                                className={item.hasChanged ? 'table-warning' : ''}
                                            >
                                                <td>{item.sku}</td>
                                                <td>
                                                    {item.itemName}
                                                    {item.size && (
                                                        <small className="text-muted d-block">
                                                            {item.size}
                                                        </small>
                                                    )}
                                                </td>
                                                <td>{item.brand}</td>
                                                <td className="text-end">
                                                    ${Number(item.unitPrice).toFixed(2)}
                                                </td>
                                                <td className="text-center">
                                                    {item.originalQuantity}
                                                </td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        className="form-control text-center"
                                                        value={item.currentQuantity}
                                                        onChange={(e) => handleQuantityChange(item.inventoryID, e.target.value)}
                                                        min="0"
                                                    />
                                                </td>
                                                <td className={`text-center ${
                                                    item.difference > 0 ? 'text-success' : 
                                                    item.difference < 0 ? 'text-danger' : ''
                                                }`}>
                                                    {item.difference > 0 ? '+' : ''}{item.difference}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
};

export default TastingRoomInventoryDetail;